import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { DateRangePicker } from 'react-date-range';
import moment from 'moment';
import MultiSelect from '../../../../commonComponents/MultiSelect';
import { states } from '../../../../commonComponents/StatePicker';
import debounceHook from '../../../../commonComponents/debounceHook';
import { DsoNameFilter } from '../../../UsersList/components/DsoNameFilter';
import { DsoRAMFilter } from '../../../UsersList/components/DsoRamFilter';
import { DsoFilter } from '../../../UsersList/OfficeAdminList/components/TableHeader/DsoFilter';
import { useAvailableSpecialties } from '../../../../../hooks/useAvailableSpecialties';
import { EducationUtils } from '../../../../../utils/EducationUtils';
import { UserSubProfession } from '../../../../../enums/UserProfession';
import { shouldUseDsoStructure } from '../../../../../growthbook';
import CloseCross from '../../../../../images/close-cross.png';

const stateOptions = states.map((el) => ({
  label: el.name,
  value: el.abbreviation,
}));

export const Filters = ({ filters, handleFilterChanged }) => {
  const allProfessionsAndSubProfessions = useSelector(
    (state) => state.jobs.allProfessionsAndSubProfessions,
  );
  const requestedEducations = useSelector((state) => state.users.requestedEducations);
  const ramUsers = useSelector((state) => state.users.ramUsers);
  const dsoManagers = useSelector((state) => state.users.dsoManagers);

  const availableRdhSpecialties = useAvailableSpecialties(requestedEducations?.RDH);
  const availableDaSpecialties = useAvailableSpecialties(requestedEducations?.DA);

  const [searchText, setSearchText] = useState(filters?.searchText || '');
  const debouncedSearchText = debounceHook(searchText, 1000);

  const [showDateRange, setShowDateRange] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: new Date(),
    key: 'selection',
  });

  const ramUsersOptions = [
    { label: 'Unassigned', value: 'unassigned' },
    ...ramUsers.map((el) => ({
      label: [el.first_name, el.last_name].join(' '),
      value: el.id,
    })),
  ];

  const dsoManagersOptions = [
    { label: 'Unassigned', value: 'unassigned' },
    ...dsoManagers.map((el) => ({
      label: [el.first_name, el.last_name].join(' '),
      value: el.id,
    })),
  ];

  const handleShowDateRangeChange = () => {
    setShowDateRange(!showDateRange);
  };

  const handleChangeDateRange = (value) => {
    setDateRange(value.selection);
  };

  const clearDateRangeSearch = () => {
    setDateRange({
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection',
    });

    handleFilterChanged('dateRange', {
      startDate: null,
      endDate: null,
      key: 'selection',
    });
  };

  const cardStyle = showDateRange
    ? {
        visibility: 'visible',
        transition: 'height 0.5s, opacity 0.7s linear',
        height: 450,
        zIndex: 10,
        backgroundColor: 'white',
        opacity: 1,
        width: '50%',
        marginTop: 16,
      }
    : {
        visibility: 'hidden',
        height: 0,
        opacity: 0,
        width: '50%',
        marginTop: 16,
        transition: 'height 0.5s, opacity 0.3s linear, visibility 0.3s',
      };

  useEffect(() => {
    if (filters.searchText !== searchText) {
      handleFilterChanged('searchText', searchText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchText]);

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ width: '100%', display: 'flex' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 16,
            width: '100%',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <p>Search:</p>
            <input
              style={{
                paddingLeft: '8px',
                fontFamily: 'Nunito-Regular',
                width: 200,
                height: 32,
              }}
              placeholder="Search"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
            />
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: 200,
            }}
          >
            <p>Status:</p>
            <select
              className="custom-select custom-select-lg"
              style={{ height: 32 }}
              value={filters.jobStatus}
              onChange={(val) => handleFilterChanged('jobStatus', val.target.value)}
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="open">Open</option>
              <option value="counter">Counter</option>
              <option value="filled">Filled</option>
              <option value="pay-out">Pay Out</option>
              <option value="cancelled">Cancelled</option>
              <option value="cancelled-hyg">Cancelled by HYG</option>
              <option value="cancelled-dnt">Cancelled by DNT</option>
              <option value="expired">Expired</option>
              <option value="complete">Complete</option>
              <option value="refill">Refill</option>
              <option value="adjusted-required">Adjusted required</option>
              <option value="pay-out-disabled">Payout Disabled</option>
              <option value="refund-disabled">Refund Disabled</option>
              <option value="pending-counter">Pending &amp; Counter</option>
            </select>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: 200,
            }}
          >
            <p>State:</p>
            <MultiSelect
              className="custom-select custom-select-lg"
              style={{ height: 32 }}
              value={
                filters.state && filters.state !== 'all'
                  ? filters.state.split(',')
                  : stateOptions.map((el) => el.value)
              }
              options={stateOptions}
              onChange={(values) => {
                handleFilterChanged(
                  'state',
                  values.length === stateOptions.length ? null : values.join(','),
                );
              }}
              placeholder="All"
              showAllButton
              autoApply={false}
            />
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: 200,
            }}
          >
            <p>Profession:</p>
            <select
              className="custom-select custom-select-lg"
              style={{ height: 32 }}
              value={filters.subProfession || filters.profession}
              onChange={(val) => {
                const optionValue = val.target.value;

                if (optionValue === UserSubProfession.EFDA) {
                  handleFilterChanged('subProfession', optionValue);
                } else {
                  handleFilterChanged('profession', optionValue !== 'all' ? optionValue : '');
                  handleFilterChanged('subProfession', '');
                }
              }}
            >
              <option value="all">All</option>
              {(allProfessionsAndSubProfessions || []).map((profession) => (
                <option key={profession.code} value={profession.code}>
                  {profession.name}
                </option>
              ))}
            </select>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: 200,
            }}
          >
            <p>RAM:</p>
            <MultiSelect
              className="custom-select custom-select-lg"
              style={{ height: 32 }}
              value={
                filters.ram && filters.ram !== 'all'
                  ? filters.ram.split(',')
                  : ramUsersOptions.map((el) => el.value)
              }
              options={ramUsersOptions}
              onChange={(values) => {
                handleFilterChanged(
                  'ram',
                  values.length === ramUsersOptions.length ? null : values.join(','),
                );
              }}
              placeholder="All"
              showAllButton
              autoApply={false}
            />
          </div>

          {shouldUseDsoStructure() ? (
            <DsoFilter
              label="DSO Name:"
              filters={filters}
              handleFilterChanged={(key, value) => handleFilterChanged(key, value)}
            />
          ) : (
            <DsoNameFilter
              filter={filters}
              handleFilterChange={handleFilterChanged}
              customStyles={{
                width: 200,
              }}
            />
          )}

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: 200,
            }}
          >
            <p>DSO Manager:</p>
            <MultiSelect
              className="custom-select custom-select-lg"
              style={{ height: 32 }}
              optionsStyle={{ width: 180 }}
              value={
                filters.dsoManager && filters.dsoManager !== 'all'
                  ? filters.dsoManager.split(',')
                  : dsoManagersOptions.map((el) => el.value)
              }
              options={dsoManagersOptions}
              onChange={(values) => {
                handleFilterChanged(
                  'dsoManager',
                  values.length === dsoManagersOptions.length ? null : values.join(','),
                );
              }}
              placeholder="All"
              showAllButton
              autoApply={false}
            />
          </div>

          <DsoRAMFilter
            filter={filters}
            handleFilterChange={handleFilterChanged}
            customStyles={{
              width: 200,
            }}
          />

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: 200,
            }}
          >
            <p>RDH Skills:</p>
            <select
              className="custom-select custom-select-lg"
              style={{ height: 32 }}
              value={filters.specialtyRdh}
              onChange={(val) =>
                handleFilterChanged('specialtyRdh', val.target.value !== 'all' ? val.target.value : '')
              }
            >
              <option value="all">All</option>
              {availableRdhSpecialties.map((specialty) => (
                <option key={specialty} value={specialty}>
                  {EducationUtils.removeSpecialtyPrefix(specialty)}
                </option>
              ))}
            </select>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: 200,
            }}
          >
            <p>DA Skills:</p>
            <select
              className="custom-select custom-select-lg"
              style={{ height: 32 }}
              value={filters.specialtyDa}
              onChange={(val) =>
                handleFilterChanged('specialtyDa', val.target.value !== 'all' ? val.target.value : '')
              }
            >
              <option value="all">All</option>
              {availableDaSpecialties.map((specialty) => (
                <option key={specialty} value={specialty}>
                  {EducationUtils.removeSpecialtyPrefix(specialty)}
                </option>
              ))}
            </select>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: 200,
            }}
          >
            <p>Worker Type:</p>
            <select
              className="custom-select custom-select-lg"
              style={{ height: 32 }}
              value={filters.workerClassification || 'all'}
              onChange={(val) => handleFilterChanged('workerClassification', val.target.value)}
            >
              <option className="dropdown-item" value="all">
                All
              </option>
              <option className="dropdown-item" value="employee">
                Employee
              </option>
              <option className="dropdown-item" value="contractor">
                Contractor
              </option>
            </select>
          </div>

          <button
            style={{
              width: 200,
              height: 32,
              justifySelf: 'flex-end',
              alignSelf: 'flex-end',
            }}
            className="btn btn-success"
            onClick={handleShowDateRangeChange}
            type="button"
          >
            Search by Date Range
          </button>
        </div>
      </div>

      {filters.startDate ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            marginLeft: 10,
            marginTop: 20,
            justifyContent: 'flex-end',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <p style={{ marginBottom: 0 }}>Selected Date Range:</p>
            <p style={{ width: 200 }}>
              {`${moment(filters.startDate).format('MM-DD-YYYY') || ''} - ${
                moment(filters.endDate).format('MM-DD-YYYY') || ''
              }`}
            </p>
          </div>
          <img
            src={CloseCross}
            style={{
              width: '20px',
              height: '20px',
              marginLeft: 5,
              marginTop: 15,
              cursor: 'pointer',
            }}
            onClick={clearDateRangeSearch}
            alt="close"
          />
        </div>
      ) : null}

      <div className="card" style={cardStyle}>
        <DateRangePicker
          style={{ marginTop: 30 }}
          ranges={[dateRange]}
          onChange={handleChangeDateRange}
        />
        <button
          style={{ width: 150, margin: 'auto' }}
          className="btn btn-success mt-2 mb-2"
          onClick={() => {
            handleFilterChanged('dateRange', dateRange);
            handleShowDateRangeChange();
          }}
          type="button"
        >
          Search
        </button>
      </div>
    </div>
  );
};
